import {
	AfterViewInit,
	Component,
	Input,
	Output,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
	EventEmitter,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AddAdventureDialog } from "../add-adventure/add-adventure.component";
import {
	CreateAdventure,
	Adventure,
	AdventureData,
} from "src/app/shared/models/adventure.model";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { Owner } from "src/environments/environment.model";
import { IAccount } from "src/app/shared/models/account.model";
import { AccountStore } from "src/app/shared/services/stores/account.store.service";
import { Observable, Subscription, catchError, forkJoin, of } from "rxjs";
import { environment } from "src/environments/environment";
import { ToastService } from "angular-toastify";
import { DeleteConfirmationDialog } from "src/app/shared/components/delete-confirmation/delete-confirmation.component";
import { AdventureService } from "src/app/shared/services/adventure.service";
import { ContentValidationDialog } from "../../lessons/content-validation/content-validation.component";
import { MetagamelinkService } from "src/app/shared/services/metagamelink.service";
import { DateTime } from "luxon";

@Component({
	selector: "app-adventure-list",
	templateUrl: "./adventure-list.component.html",
	styleUrls: ["./adventure-list.component.scss"],
})
export class AdventureListComponent
	implements AfterViewInit, OnInit, OnChanges, OnDestroy
{
	@Input("adventures") adventures!: Adventure[];
	@Input("oppositeAdventures") oppositeAdventures!: Adventure[];
	@Input("isDraft") isDraft!: boolean;
	@Output("adventureUpdated") adventureUpdate = new EventEmitter<boolean>();

	displayedColumns: string[] = [
		"name",
		"actions",
		"status",
		"image",
		`lessons`,
		"age",
		"recommendation",
		"owner",
		"version",
	];

	@ViewChild(MatPaginator) paginator!: MatPaginator;
	defaultPageSize!: number;

	dataSource = new MatTableDataSource<Adventure>([]);
	adventuresSelected: { [key: string]: boolean } = {};
	draftAhead: { [key: string]: { isAhead: boolean; version: number } } = {};
	noLive: string[] = [];
	isAllSelected: boolean = false;
	owners!: Owner[];

	subscriptions: Subscription;

	constructor(
		private dialog: MatDialog,
		private accountStore: AccountStore,
		private toastService: ToastService,
		private adventureService: AdventureService,
		private metagameLinkService: MetagamelinkService
	) {
		this.subscriptions = new Subscription();
	}

	ngOnInit() {
		const accountSub = this.accountStore.listAccounts().subscribe((res) => {
			if (res == null) {
				return;
			}
			const accountOwners: Owner[] = (res as IAccount[])
				.sort((a, b) => a.displayName.localeCompare(b.displayName))
				.map(acc => ({ id: `accounts/${acc.uid}`, displayName: acc.displayName }));
			this.owners = [...environment.KNOWN_OWNERS, ...accountOwners];
		});

		this.subscriptions.add(accountSub);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.adventures || changes.oppositeAdventures) {
			this.dataSource = new MatTableDataSource(this.adventures);
			this.noLive = [];

			this.isAllSelected = false;

			this.adventures.forEach((adventure) => {
				this.adventuresSelected[adventure.uid] = false;
				const oppositeAdventure = this.oppositeAdventures.find(
					(l) => l.uid === adventure.uid
				);
				if (!oppositeAdventure) this.noLive.push(adventure.uid);
				this.draftAhead[adventure.uid] = {
					isAhead: oppositeAdventure
						? !(oppositeAdventure.version === adventure.version)
						: true,
					version: oppositeAdventure ? oppositeAdventure.version : -1,
				};
			});

			if (this.paginator) {
				this.setPaginator();
			}
		}

		if (changes.isDraft) {
			this.defaultPageSize = this.isDraft
				? +(sessionStorage.getItem("draft-adventuresPageSize") ?? 10)
				: +(sessionStorage.getItem("live-adventuresPageSize") ?? 10);
		}
	}

	ngAfterViewInit(): void {
		this.setPaginator();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	createImage(id: string) {
		if (!id) {
			return `https://storage.googleapis.com/${environment.BUCKETS.CONTENT_BUCKET}/images/image_default.jpg`;
		}
		return `https://storage.googleapis.com/${environment.BUCKETS.CONTENT_BUCKET}/images/${id}.jpg`;
	}

	//#region GLOBAL ACTIONS

	openContentValidation(adventure: Adventure) {
		this.dialog.open(ContentValidationDialog, {
			width: "650px",
			data: adventure as any,
		});
	}

	openAddDialog() {
		const dialogRef = this.dialog.open(AddAdventureDialog, {
			disableClose: true,
			data: {
				name: "",
				imageId: "",
				age: "2+",
				lessons: [],
				recommendation: {
					type: "Classic",
				},
				owner: "",
				version: 1,
				releasePhase: "Published",
			} as CreateAdventure,
		});

		dialogRef.afterClosed().subscribe((result: Adventure) => {
			if (!result) {
				return;
			}

			this.toastService.success(
				`Adventure ${result.uid} created successfully`
			);
			this.adventureUpdate.emit(true);
		});
	}

	selectAll(event: MatCheckboxChange) {
		this.isAllSelected = event.checked;
		for (let adventureId in this.adventuresSelected) {
			this.adventuresSelected[adventureId] = event.checked;
		}
	}

	deleteAdventure() {
		const selectedIds = this.getSelected();
		if (selectedIds.length === 0) {
			this.toastService.error(
				`You must select at least a single lesson to delete!`
			);
			return;
		}

		if (selectedIds.length > 5) {
			this.toastService.error(`You can only delete 5 lessons at a time!`);
			return;
		}

		const dialogRef = this.dialog.open(DeleteConfirmationDialog, {
			width: "350px",
		});

		dialogRef.afterClosed().subscribe((result: boolean) => {
			if (!result) {
				return;
			}

			const adventureDeleteRequests: Observable<boolean | void>[] = [];
			selectedIds.forEach((id: string) => {
				const request = this.adventureService.removeAdventure(id).pipe(
					catchError((err) => {
						this.toastService.error(
							`Failed to delete adventure with id ${id}`
						);
						return of(false);
					})
				);

				adventureDeleteRequests.push(request);
			});

			forkJoin([...adventureDeleteRequests]).subscribe({
				next: (res) => {
					if (!res.includes(false)) {
						this.toastService.success(
							`Deleted adventures successfully`
						);
						this.adventureUpdate.emit(true);
					}
				},
			});
		});
	}

	publishAdventures(release: boolean) {
		const selectedIds = this.getSelected();
		if (selectedIds.length === 0) {
			this.toastService.error(
				`You must select at least a single adventure to publish/un-publish!`
			);
			return;
		}

		const requests: Observable<boolean>[] = [];

		selectedIds.forEach((adventureId) => {
			const request = this.adventureService
				.publishAdventure(adventureId, release)
				.pipe(
					catchError((error) => {
						this.toastService.error(
							`Failed to publish adventure ${adventureId}`
						);
						console.log(
							`Failed to publish adventureId ${adventureId}`,
							error
						);
						return of(false);
					})
				);
			requests.push(request);
		});

		forkJoin(requests).subscribe({
			next: (res) => {
				if (!res.includes(false)) {
					this.toastService.success("Adventures updated successfully!");
					this.adventureUpdate.emit(true);
				}
			},
		});
	}

	pushToLive() {
		const selectedIds = this.getSelected();

		if (selectedIds.length === 0) {
			this.toastService.error(
				`You must select at least one lesson to push!`
			);
			return;
		}

		for (let id of selectedIds) {
			const selected = this.adventures.find(
				(adventure) => adventure.uid === id
			);
			if (selected && !selected.isContentValid) {
				this.toastService.error(
					`Adventure ${selected.name} has invalid content, please fix it or remove it from selected before pushing to live!`
				);
				return;
			}
		}

		this.adventureService.pushAdventuresToLive(selectedIds).subscribe({
			next: () => {
				this.toastService.success(`Adventures pushed to live correctly.`);
				this.adventureUpdate.emit(true);
			},
			error: () => {
				this.toastService.error(`Failed to push adventures live!`);
			},
		});
	}

	//#endregion

	//#region ADVENTURE ACTIONS

	select(event: MatCheckboxChange, checkedId: string) {
		this.isAllSelected = false;
		this.adventuresSelected[checkedId] = event.checked;
	}

	editAdventure(uid: string, adventure: Adventure) {
		const dialogRef = this.dialog.open(AddAdventureDialog, {
			disableClose: true,
			data: {
				uid,
				name: adventure.name,
				imageId: adventure.imageId,
				age: adventure.age,
				lessons: adventure.lessons,
				recommendation: adventure.recommendation,
				owner: adventure.owner,
				version: adventure.version,
			} as Adventure,
		});

		dialogRef.afterClosed().subscribe((result: Adventure) => {
			if (!result) {
				return;
			}

			this.toastService.success(
				`Adventure ${result.uid} updated successfully`
			);
			this.adventureUpdate.emit(true);
		});
	}

	getOwner(ownerId: string) {
		let owner = this.owners.find((obj) => obj.id === ownerId);
		return owner ? owner.displayName : ownerId;
	}

	testAdventure(uid: string) {
		this.metagameLinkService.testAdventure(uid, this.isDraft);
	}

	getDate(date: string) {
		return DateTime.fromISO(date).toFormat('dd-MM-yyyy HH:mm');
	}

	changeQAValidation(uid: string, isValid: boolean) {
		this.adventureService.validateAdventure(uid, isValid).subscribe({
			next: () => {
				this.toastService.success(`Adventures ${uid} was ${isValid ? 'validated' : 'invalidated'} successfully`);
				this.adventureUpdate.emit(true);
			},
			error: () => {
				this.toastService.error(`Failed to change adventure ${uid} validation!`);
			},
		});
	}

	//#endregion

	private setPaginator() {
		this.dataSource.paginator = this.paginator;
		this.paginator.pageIndex = this.isDraft
			? +(sessionStorage.getItem("draft-adventurePageIndex") ?? 0)
			: +(sessionStorage.getItem("live-adventurePageIndex") ?? 0);
	}

	paginatorChanged(event: any) {
		const mode = this.isDraft ? "draft" : "live";
		sessionStorage.setItem(`${mode}-adventuresPageSize`, event.pageIndex);
		sessionStorage.setItem(`${mode}-adventuresPageSize`, event.pageSize);
	}

	private getSelected() {
		const selectedIds = [];

		for (let id in this.adventuresSelected) {
			const isFiltered = this.dataSource.filteredData.find(
				(lesson) => lesson.uid === id
			);
			if (this.adventuresSelected[id] && isFiltered) {
				selectedIds.push(id);
			}
		}

		return selectedIds;
	}
}
